import { useQuery } from '@tanstack/react-query';
import { queries } from '../../../../queries';
import { useAPIErrorHandler } from '../../useAPIErrorHandler';
import type { Nullable } from '../../../../types/common/utility';
import type { SubmissionDetailsDto } from '../../../../api/submissions/submissions.api.types';

type UseGetSubmissionByIdOptions = {
  refetchInterval?:
    | number
    | false
    | ((data: SubmissionDetailsDto | undefined) => number | false);
};

export const useGetSubmissionById = (
  submissionId: Nullable<string>,
  options?: UseGetSubmissionByIdOptions
) => {
  const handleAPIError = useAPIErrorHandler();
  return useQuery({
    ...queries.submissions.details(submissionId),
    keepPreviousData: true,
    onError: handleAPIError('Unable to fetch submission details'),
    retry: false,
    enabled: !!submissionId,
    refetchInterval: options?.refetchInterval,
  });
};

export const useGetSubmissionWithConfidenceById = (
  submissionId: Nullable<string>
) => {
  const handleAPIError = useAPIErrorHandler();
  return useQuery({
    ...queries.submissions.detailsWithConfidence(submissionId),
    keepPreviousData: true,
    onError: handleAPIError('Unable to fetch submission details'),
    retry: false,
    enabled: !!submissionId,
  });
};

export const useGetSubmissionByMsgId = (msgId: string, enabled = true) => {
  return useQuery({
    ...queries.submissions.detailsByMsgId(msgId),
    keepPreviousData: true,
    retry: false,
    suspense: false,
    useErrorBoundary: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: 60000, // Consider data fresh for 1 minute
    enabled: enabled && !!msgId,
  });
};

export const useGetSubmissionEligibleProductsAcrossLines = (
  submissionId: string
) => {
  const handleAPIError = useAPIErrorHandler();
  return useQuery({
    ...queries.submissions.eligibleProductsAcrossLines(submissionId),
    keepPreviousData: true,
    onError: handleAPIError('Unable to fetch eligible products for submission'),
    retry: false,
    enabled: !!submissionId,
  });
};

/**
 * The submission's questions as one merged form for the products its coverage options name; off while they name
 * none. Off means no form at all: a disabled query still hands back what the cache holds, from before the last
 * product was removed.
 */
export const useGetSubmissionMergedQuestions = (
  submissionId: string,
  productKeys: readonly string[]
) => {
  const handleAPIError = useAPIErrorHandler();
  const enabled = productKeys.length > 0;
  const query = useQuery({
    ...queries.submissions.mergedQuestions(submissionId, productKeys),
    onError: handleAPIError('Unable to load underwriting questions'),
    retry: false,
    // A product change starts a new key: the previous form stays until the new one arrives, so it never reads as a
    // first load and the router's spinner never unmounts the page mid-edit.
    keepPreviousData: true,
    // A failed load is not retried on remount: the router's spinner remounts the page while this loads, so a retry
    // there loops on an error.
    retryOnMount: false,
    enabled: enabled && !!submissionId,
  });
  // The previous form is kept only for this submission: its id leads with the submission it was merged for.
  const isThisSubmissions =
    !query.data || query.data.id.startsWith(`${submissionId}:`);
  return enabled && isThisSubmissions ? query : { ...query, data: undefined };
};

export const useGetSubmissionEligibleProducts = (submissionId: string) => {
  const handleAPIError = useAPIErrorHandler();
  return useQuery({
    ...queries.submissions.eligibleProducts(submissionId),
    keepPreviousData: true,
    onError: handleAPIError('Unable to fetch eligible products for submission'),
    retry: false,
    enabled: !!submissionId,
  });
};
